import { SettingOutlined } from '@ant-design/icons';
import { CompassIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { type SettingSidebarMenuT, useSettingTabItems } from '@/entities/settings';
import { canShowContactsMenu } from '@/entities/workspace';
import { getWorkspaceNavigatorLeafItems } from '@/features/Commons/Sidebar/model/sidebar-schema';
import { useFaceCream } from '@/hooks/domain/customWorkspace/useFaceCream';
import useUserRole from '@/hooks/domain/useUserRole';
import { isProdMode } from '@/utils/environment';

import { NAVIGATOR_TARGET, type NavigatorSelectItem } from '../types/navigatorTypes';

const useNavigateSiteMap = () => {
  const settingItems = useSettingTabItems();
  const params = useParams<{ workspace_id?: string }>();
  const isFaceCream = useFaceCream();
  const { isEditor } = useUserRole();
  const showCustomerMenu = canShowContactsMenu(params.workspace_id);

  const menuItems = useMemo(() => {
    return getWorkspaceNavigatorLeafItems({
      isEditor,
      isFaceCream,
      isProdMode: isProdMode(),
      hideCustomer: !showCustomerMenu,
    })
      .map((item) => {
        return {
          name: item.label,
          icon: item.IconComponent ? (
            <item.IconComponent width={20} height={20} />
          ) : (
            <CompassIcon width={20} height={20} />
          ),
          target: NAVIGATOR_TARGET.MENU,
          hidden: false,
          alias: item.href,
          href: item.href,
        };
      });
  }, [isEditor, isFaceCream, showCustomerMenu]);

  const setttingItemList: NavigatorSelectItem[] = useMemo(() => {
    return Object.keys(settingItems).map((key) => {
      const item = settingItems[key as SettingSidebarMenuT];
      const isHidden = () => {
        if (item.hidden) {
          return item.hidden();
        }
        return false;
      };
      return {
        name: item.text,
        icon: <SettingOutlined style={{ fontSize: 16 }} />,
        target: NAVIGATOR_TARGET.SETTINGS,
        hidden: isHidden(),
        path: 'settings',
        alias: key,
        href: `/settings/${key}`,
      };
    });
  }, [settingItems]);

  const result: NavigatorSelectItem[] = useMemo(() => {
    return [...setttingItemList, ...menuItems];
  }, [menuItems, setttingItemList]);

  return result;
};

export default useNavigateSiteMap;
